import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from 'react-query'
import { authorsAPI } from '../api/client'
import { Check, X, Users, ExternalLink, Pause, RotateCcw, Eye, Zap } from 'lucide-react'

// Status config with colors and labels
const STATUS_CONFIG = {
  pending: { label: 'Ожидает', color: 'bg-yellow-500/20 text-yellow-400' },
  approved_auto: { label: 'Авто', color: 'bg-green-500/20 text-green-400' },
  approved_manual: { label: 'Премод', color: 'bg-blue-500/20 text-blue-400' },
  rejected: { label: 'Отклонён', color: 'bg-red-500/20 text-red-400' },
  paused: { label: 'Пауза', color: 'bg-gray-500/20 text-gray-400' },
}

function AuthorCard({ author, onAction, isLoading }) {
  const status = author.status
  const statusConfig = STATUS_CONFIG[status] || STATUS_CONFIG.pending

  return (
    <div className="glass p-4">
      {/* Header */}
      <div className="flex items-center gap-3 mb-4">
        {author.avatar_url ? (
          <img src={author.avatar_url} alt="" className="w-12 h-12 rounded-full" />
        ) : (
          <div className="w-12 h-12 rounded-full bg-white/10 flex items-center justify-center">
            <Users size={24} />
          </div>
        )}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <h3 className="font-semibold truncate">{author.author_name || 'Unknown'}</h3>
            {author.author_url && (
              <a
                href={author.author_url}
                target="_blank"
                rel="noopener noreferrer"
                className="text-primary hover:text-secondary"
              >
                <ExternalLink size={16} />
              </a>
            )}
          </div>
          <p className="text-sm opacity-50">{author.images_count} images</p>
        </div>
        <span className={`px-2 py-1 rounded text-xs font-medium ${statusConfig.color}`}>
          {statusConfig.label}
        </span>
      </div>

      {/* Preview images */}
      {author.preview_images?.length > 0 && (
        <div className="grid grid-cols-3 gap-2 mb-4">
          {author.preview_images.slice(0, 6).map(img => (
            <div key={img.id} className="aspect-square rounded-lg overflow-hidden bg-white/5">
              <img
                src={img.thumbnail_url}
                alt=""
                className="w-full h-full object-cover"
                loading="lazy"
              />
            </div>
          ))}
        </div>
      )}

      {/* Actions based on status */}
      <div className="flex flex-wrap gap-2">
        {/* PENDING: show approve options and reject */}
        {status === 'pending' && (
          <>
            <button
              onClick={() => onAction(author.id, 'approveAuto')}
              disabled={isLoading}
              className="btn-success flex-1 flex items-center justify-center gap-1 text-sm py-2"
              title="Авто-одобрение всех картинок"
            >
              <Zap size={16} />
              Авто
            </button>
            <button
              onClick={() => onAction(author.id, 'approveManual')}
              disabled={isLoading}
              className="btn-primary flex-1 flex items-center justify-center gap-1 text-sm py-2"
              title="Одобрение с премодерацией картинок"
            >
              <Eye size={16} />
              Премод
            </button>
            <button
              onClick={() => onAction(author.id, 'reject')}
              disabled={isLoading}
              className="btn-danger flex-1 flex items-center justify-center gap-1 text-sm py-2"
            >
              <X size={16} />
              Отклонить
            </button>
          </>
        )}

        {/* APPROVED_AUTO: can change to manual, pause, or reject */}
        {status === 'approved_auto' && (
          <>
            <button
              onClick={() => onAction(author.id, 'approveManual')}
              disabled={isLoading}
              className="btn-primary flex-1 flex items-center justify-center gap-1 text-sm py-2"
              title="Переключить на премодерацию"
            >
              <Eye size={16} />
              → Премод
            </button>
            <button
              onClick={() => onAction(author.id, 'pause')}
              disabled={isLoading}
              className="btn-ghost flex items-center justify-center gap-1 text-sm py-2 px-3"
              title="Поставить на паузу"
            >
              <Pause size={16} />
            </button>
            <button
              onClick={() => onAction(author.id, 'reject')}
              disabled={isLoading}
              className="btn-danger flex items-center justify-center gap-1 text-sm py-2 px-3"
              title="Отклонить автора"
            >
              <X size={16} />
            </button>
          </>
        )}

        {/* APPROVED_MANUAL: can change to auto, pause, or reject */}
        {status === 'approved_manual' && (
          <>
            <button
              onClick={() => onAction(author.id, 'approveAuto')}
              disabled={isLoading}
              className="btn-success flex-1 flex items-center justify-center gap-1 text-sm py-2"
              title="Переключить на авто-одобрение"
            >
              <Zap size={16} />
              → Авто
            </button>
            <button
              onClick={() => onAction(author.id, 'pause')}
              disabled={isLoading}
              className="btn-ghost flex items-center justify-center gap-1 text-sm py-2 px-3"
              title="Поставить на паузу"
            >
              <Pause size={16} />
            </button>
            <button
              onClick={() => onAction(author.id, 'reject')}
              disabled={isLoading}
              className="btn-danger flex items-center justify-center gap-1 text-sm py-2 px-3"
              title="Отклонить автора"
            >
              <X size={16} />
            </button>
          </>
        )}

        {/* REJECTED: can restore to pending */}
        {status === 'rejected' && (
          <button
            onClick={() => onAction(author.id, 'restore')}
            disabled={isLoading}
            className="btn-primary flex-1 flex items-center justify-center gap-2 text-sm py-2"
          >
            <RotateCcw size={16} />
            Восстановить
          </button>
        )}

        {/* PAUSED: can resume to previous approved state or reject */}
        {status === 'paused' && (
          <>
            <button
              onClick={() => onAction(author.id, 'approveAuto')}
              disabled={isLoading}
              className="btn-success flex-1 flex items-center justify-center gap-1 text-sm py-2"
              title="Возобновить с авто-одобрением"
            >
              <Zap size={16} />
              Авто
            </button>
            <button
              onClick={() => onAction(author.id, 'approveManual')}
              disabled={isLoading}
              className="btn-primary flex-1 flex items-center justify-center gap-1 text-sm py-2"
              title="Возобновить с премодерацией"
            >
              <Eye size={16} />
              Премод
            </button>
            <button
              onClick={() => onAction(author.id, 'reject')}
              disabled={isLoading}
              className="btn-danger flex items-center justify-center gap-1 text-sm py-2 px-3"
            >
              <X size={16} />
            </button>
          </>
        )}
      </div>
    </div>
  )
}

export default function Authors() {
  const queryClient = useQueryClient()
  const [filter, setFilter] = useState('pending')
  const [selectedIds, setSelectedIds] = useState([])

  // Map filter to actual status values
  const getStatusFilter = () => {
    if (filter === 'all') return undefined
    if (filter === 'approved') return undefined // Will filter client-side
    return filter
  }

  const { data: authors, isLoading } = useQuery(
    ['authors', filter],
    async () => {
      const data = await authorsAPI.getAll({
        status: getStatusFilter(),
        limit: 100
      })
      // Client-side filter for "approved" tab (both auto and manual)
      if (filter === 'approved') {
        return data.filter(a => a.status === 'approved_auto' || a.status === 'approved_manual')
      }
      return data
    }
  )

  const { data: stats } = useQuery('authorStats', authorsAPI.getStats, {
    refetchInterval: 30000
  })

  // Single mutation for all actions
  const actionMutation = useMutation(
    async ({ id, action }) => {
      switch (action) {
        case 'approveAuto': return authorsAPI.approveAuto(id)
        case 'approveManual': return authorsAPI.approveManual(id)
        case 'reject': return authorsAPI.reject(id)
        case 'pause': return authorsAPI.pause(id)
        case 'restore': return authorsAPI.restore(id)
        default: throw new Error(`Unknown action: ${action}`)
      }
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries('authors')
        queryClient.invalidateQueries('authorStats')
      }
    }
  )

  const bulkMutation = useMutation(authorsAPI.bulkAction, {
    onSuccess: () => {
      queryClient.invalidateQueries('authors')
      queryClient.invalidateQueries('authorStats')
      setSelectedIds([])
    }
  })

  const handleAction = (id, action) => {
    actionMutation.mutate({ id, action })
  }

  const handleBulkAction = (action) => {
    if (selectedIds.length === 0) return
    bulkMutation.mutate({ author_ids: selectedIds, action })
  }

  // Filter tabs with counts
  const filterTabs = [
    { key: 'pending', label: 'Ожидают', count: stats?.pending },
    { key: 'approved', label: 'Одобрены', count: (stats?.approved_auto || 0) + (stats?.approved_manual || 0) },
    { key: 'approved_auto', label: 'Авто', count: stats?.approved_auto },
    { key: 'approved_manual', label: 'Премод', count: stats?.approved_manual },
    { key: 'rejected', label: 'Отклонены', count: stats?.rejected },
    { key: 'paused', label: 'Пауза', count: stats?.paused },
    { key: 'all', label: 'Все', count: stats?.total },
  ]

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold">Авторы</h1>

        {/* Bulk actions */}
        {selectedIds.length > 0 && (
          <div className="flex items-center gap-2">
            <span className="text-sm opacity-60">{selectedIds.length} выбрано</span>
            <button
              onClick={() => handleBulkAction('approve_auto')}
              className="btn-success"
              disabled={bulkMutation.isLoading}
            >
              Авто
            </button>
            <button
              onClick={() => handleBulkAction('approve_manual')}
              className="btn-primary"
              disabled={bulkMutation.isLoading}
            >
              Премод
            </button>
            <button
              onClick={() => handleBulkAction('reject')}
              className="btn-danger"
              disabled={bulkMutation.isLoading}
            >
              Отклонить
            </button>
          </div>
        )}
      </div>

      {/* Filter tabs */}
      <div className="flex flex-wrap gap-2 mb-6">
        {filterTabs.map(tab => (
          <button
            key={tab.key}
            onClick={() => setFilter(tab.key)}
            className={`btn-ghost flex items-center gap-2 ${filter === tab.key ? 'bg-primary/20 text-primary' : ''}`}
          >
            {tab.label}
            {tab.count !== undefined && (
              <span className="text-xs opacity-60">({tab.count})</span>
            )}
          </button>
        ))}
      </div>

      {/* Authors grid */}
      {isLoading ? (
        <div className="text-center py-12 opacity-50">Загрузка...</div>
      ) : authors?.length === 0 ? (
        <div className="text-center py-12 opacity-50">Авторы не найдены</div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {authors?.map(author => (
            <AuthorCard
              key={author.id}
              author={author}
              onAction={handleAction}
              isLoading={actionMutation.isLoading}
            />
          ))}
        </div>
      )}
    </div>
  )
}
